import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, Building2, Calculator, CheckCircle, Crown, TrendingUp } from 'lucide-react';

export default function FoundingClubTab({ customClubRevenue, setCustomClubRevenue, foundingClubFee, setFoundingClubFee, utilityMargin, customClubUtilityFee, foundingClubRemuneration, formatCurrency }) {
  return (
    <>
              {/* Concept */}
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <Crown className="w-5 h-5" />
                    The Founding Club Advantage
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-amber-900">
                  <p className="mb-4">
                    <strong>AADC as the founding club</strong> bears the cost and risk of building this platform. 
                    Newer clubs created after the platform is successful will benefit from:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>A proven, stable platform (no beta testing required)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Established features and workflows (years of refinement)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Network effects (cross-club discovery, shared resources)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>Lower risk (proven business model)</span>
                    </li>
                  </ul>
                  <p className="text-sm bg-amber-100 p-3 rounded-lg">
                    <strong>The Question:</strong> Should newer clubs contribute a small fee to AADC as recognition 
                    of the founding club's investment in building the platform? This is separate from VBB's service fees.
                  </p>
                </CardContent>
              </Card>

              {/* Founding Club Fee Calculator */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Founding Club Remuneration Calculator
                  </CardTitle>
                  <CardDescription>
                    Model different scenarios for newer clubs contributing to AADC
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Newer Club Revenue Input */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">New Club Monthly Revenue</Label>
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={customClubRevenue}
                        onChange={(e) => setCustomClubRevenue(Number(e.target.value))}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </div>

                  {/* Founding Club Fee Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium">AADC Founding Club Fee</Label>
                      <span className="text-2xl font-bold text-amber-600">{foundingClubFee}%</span>
                    </div>
                    <Slider
                      value={[foundingClubFee]}
                      onValueChange={(v) => setFoundingClubFee(v[0])}
                      min={0}
                      max={5}
                      step={0.5}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0% (No fee)</span>
                      <span className="text-amber-600 font-medium">1-2% (Recommended)</span>
                      <span>5% (Maximum)</span>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                    <Card className="bg-amber-100 border-amber-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-amber-700">AADC Receives</p>
                        <p className="text-2xl font-bold text-amber-800">{formatCurrency(foundingClubRemuneration)}</p>
                        <p className="text-xs text-amber-600">/month per new club</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-100 border-blue-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-blue-700">VBB Receives (Utility)</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(customClubUtilityFee)}</p>
                        <p className="text-xs text-blue-600">/month (cost + {utilityMargin}%)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-100 border-green-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-green-700">New Club Retains</p>
                        <p className="text-2xl font-bold text-green-800">
                          {formatCurrency(customClubRevenue - customClubUtilityFee - foundingClubRemuneration)}
                        </p>
                        <p className="text-xs text-green-600">/month net</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Fee Breakdown */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium mb-3">Complete Fee Breakdown for New Club</p>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Club Gross Revenue</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(customClubRevenue)}</TableCell>
                          <TableCell className="text-right">100%</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-blue-600">− VBB Platform Fee (Utility)</TableCell>
                          <TableCell className="text-right text-blue-600">({formatCurrency(customClubUtilityFee)})</TableCell>
                          <TableCell className="text-right text-blue-600">
                            {((customClubUtilityFee / customClubRevenue) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-amber-600">− AADC Founding Fee</TableCell>
                          <TableCell className="text-right text-amber-600">({formatCurrency(foundingClubRemuneration)})</TableCell>
                          <TableCell className="text-right text-amber-600">{foundingClubFee}%</TableCell>
                        </TableRow>
                        <TableRow className="border-t-2">
                          <TableCell className="font-bold text-green-600">= Club Net Retained</TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(customClubRevenue - customClubUtilityFee - foundingClubRemuneration)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {(((customClubRevenue - customClubUtilityFee - foundingClubRemuneration) / customClubRevenue) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Governance Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Governance Options
                  </CardTitle>
                  <CardDescription>
                    "In the long run, this may be up to the clubs to work out"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option A: Platform-Level Fee</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>VBB collects the founding club fee as part of the service agreement and remits to AADC.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Simple, automatic collection
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Transparent to all parties
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            May be seen as "double dipping"
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option B: Club-to-Club Agreement</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>New clubs negotiate directly with AADC, separate from VBB relationship.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Club autonomy preserved
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Relationship-based, flexible
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            More complex, may be ignored
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option C: Network Membership Fee</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>All clubs pay a small fee to a "Club Network Fund" managed by club representatives.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Democratic governance
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            AADC recognized but not sole beneficiary
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            Requires governance infrastructure
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Option D: Time-Limited Recognition</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <p>Founding club fee applies only to clubs joining within first 3-5 years of platform launch.</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Recognizes early risk-taking
                          </li>
                          <li className="flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-1" />
                            Natural sunset provision
                          </li>
                          <li className="flex items-start gap-1">
                            <AlertCircle className="w-3 h-3 text-amber-500 mt-1" />
                            Creates "deadline" dynamics
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Note:</strong> This is a governance question that the clubs themselves may need to decide. 
                      VBB can facilitate the framework, but the decision on whether (and how) newer clubs recognize 
                      the founding club's contributions should ultimately be a matter for the club community.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Projection Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Founding Club Revenue Projection
                  </CardTitle>
                  <CardDescription>
                    AADC income from newer clubs at different network sizes (at {foundingClubFee}% fee)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Network Size</TableHead>
                        <TableHead>Avg Club Revenue</TableHead>
                        <TableHead>AADC Monthly Income</TableHead>
                        <TableHead>AADC Annual Income</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { clubs: 5, avgRev: 5000 },
                        { clubs: 10, avgRev: 8000 },
                        { clubs: 25, avgRev: 10000 },
                        { clubs: 50, avgRev: 12000 },
                        { clubs: 100, avgRev: 15000 }
                      ].map((row, idx) => {
                        const otherClubs = row.clubs - 1; // Excluding AADC itself
                        const monthlyIncome = otherClubs * row.avgRev * (foundingClubFee / 100);
                        return (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.clubs} clubs</TableCell>
                            <TableCell>${row.avgRev.toLocaleString()}/mo</TableCell>
                            <TableCell className="text-amber-600 font-medium">{formatCurrency(monthlyIncome)}</TableCell>
                            <TableCell className="text-amber-600 font-medium">{formatCurrency(monthlyIncome * 12)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <p className="text-xs text-muted-foreground mt-4">
                    * Assumes all clubs except AADC pay the founding club fee. Actual adoption may vary.
                  </p>
                </CardContent>
              </Card>
    </>
  );
}
